import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  MessageSquare, 
  TrendingUp, 
  TrendingDown,
  Filter,
  Search,
  AlertCircle,
  User
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const VarianceAlerts = ({ alerts, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");
  
  // Acknowledgment modal state
  const [acknowledgingAlert, setAcknowledgingAlert] = useState(null);
  const [acknowledgmentComment, setAcknowledgmentComment] = useState("");

  const handleAcknowledgeAlert = async (alertId) => {
    if (!acknowledgmentComment.trim()) {
      setError("Please provide a comment explaining this variance.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/acknowledge-variance/${alertId}`, {
        acknowledged_by: "user",
        comment: acknowledgmentComment
      });

      setAcknowledgingAlert(null);
      setAcknowledgmentComment("");
      onRefresh(); // Refresh the alerts list
    } catch (e) {
      setError(e.response?.data?.detail || "Error acknowledging variance alert");
    } finally {
      setLoading(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        alert.rate_type?.toLowerCase().includes(searchLower) ||
        alert.alert_type?.toLowerCase().includes(searchLower) ||
        (alert.acknowledgment_comment || '').toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (filterStatus !== "all") {
      if (filterStatus === "acknowledged" && !alert.acknowledged) return false;
      if (filterStatus === "unacknowledged" && alert.acknowledged) return false;
    }

    // Severity filter
    if (filterSeverity !== "all" && alert.severity !== filterSeverity) {
      return false;
    }

    return true;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getVarianceIcon = (alertType, variance) => {
    if (alertType === "rate_variance") {
      return variance > 0 ? 
        <TrendingUp className="h-4 w-4 text-red-600" /> : 
        <TrendingDown className="h-4 w-4 text-red-600" />;
    }
    return <AlertTriangle className="h-4 w-4 text-orange-600" />;
  };

  const formatVariance = (alert) => {
    if (alert.alert_type === "rate_variance") {
      return `${alert.variance_percent > 0 ? '+' : ''}${alert.variance_percent?.toFixed(2)}%`;
    } else {
      return `$${alert.variance_dollar?.toLocaleString()}`;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Alert Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-orange-900">Total Alerts</p>
                <p className="text-2xl font-bold text-orange-600">{alerts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-sm font-medium text-red-900">Critical</p>
                <p className="text-2xl font-bold text-red-600">
                  {alerts.filter(a => a.severity === 'critical').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Unacknowledged</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {alerts.filter(a => !a.acknowledged).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-900">Acknowledged</p>
                <p className="text-2xl font-bold text-green-600">
                  {alerts.filter(a => a.acknowledged).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 relative min-w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search alerts by rate type or comment..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-slate-600" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Alerts</SelectItem>
                  <SelectItem value="unacknowledged">Unacknowledged</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-slate-600" />
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <span>Variance Alerts</span>
            <Badge variant="outline">{filteredAlerts.length} alerts</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert className="border-red-200 bg-red-50 mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-700">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {filteredAlerts.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-300" />
              <h3 className="text-lg font-medium mb-2 text-slate-600">
                {alerts.length === 0 ? "No Variance Alerts" : "No Alerts Match Filters"}
              </h3>
              <p className="text-sm text-slate-500">
                {alerts.length === 0 
                  ? "All rates are within acceptable variance thresholds." 
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Alert Type</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Current vs Baseline</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAlerts.map((alert) => (
                    <TableRow key={alert._id || alert.id}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getVarianceIcon(alert.alert_type, alert.variance_percent || alert.variance_dollar)}
                          <span className="font-medium">
                            {alert.alert_type === "rate_variance" ? "Rate Variance" : 
                             alert.alert_type === "pool_variance" ? "Pool Variance" : "Base Variance"}
                          </span>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {alert.rate_type} {alert.alert_type === "rate_variance" ? "Rate" : "Pool"}
                        </Badge>
                      </TableCell>
                      
                      <TableCell>
                        <div className="text-sm">
                          <div>Current: {alert.alert_type === "rate_variance" ? 
                            `${alert.current_value?.toFixed(2)}%` : 
                            `$${alert.current_value?.toLocaleString()}`}
                          </div>
                          <div className="text-slate-500">
                            Baseline: {alert.alert_type === "rate_variance" ? 
                              `${alert.baseline_value?.toFixed(2)}%` : 
                              `$${alert.baseline_value?.toLocaleString()}`}
                          </div>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className={`font-bold ${
                          alert.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {formatVariance(alert)}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getSeverityIcon(alert.severity)}
                          <Badge className={getSeverityColor(alert.severity)}>
                            {alert.severity}
                          </Badge>
                        </div>
                      </TableCell>
                      
                      <TableCell className="text-sm text-slate-500">
                        {new Date(alert.created_timestamp).toLocaleString()}
                      </TableCell>
                      
                      <TableCell>
                        {alert.acknowledged ? (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <div className="text-sm">
                              <div className="text-green-600 font-medium">Acknowledged</div>
                              <div className="text-slate-500">by {alert.acknowledged_by}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-yellow-600" />
                            <span className="text-yellow-600 font-medium text-sm">Pending</span>
                          </div>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        {!alert.acknowledged ? (
                          <Button
                            size="sm"
                            onClick={() => setAcknowledgingAlert(alert)}
                            disabled={loading}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Acknowledge
                          </Button>
                        ) : (
                          <div className="text-sm text-slate-500 max-w-40">
                            {alert.acknowledgment_comment ? 
                              `"${alert.acknowledgment_comment.substring(0, 50)}..."` : 
                              "No comment"
                            }
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acknowledgment Modal */}
      {acknowledgingAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <MessageSquare className="h-5 w-5" />
              <span>Acknowledge Variance Alert</span>
            </h3>
            
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded">
                <div className="text-sm font-medium">
                  {acknowledgingAlert.rate_type.toUpperCase()} {acknowledgingAlert.alert_type === "rate_variance" ? "Rate" : "Pool"} Variance
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Current: {acknowledgingAlert.alert_type === "rate_variance" ? 
                    `${acknowledgingAlert.current_value?.toFixed(2)}%` : 
                    `$${acknowledgingAlert.current_value?.toLocaleString()}`}
                  {" | "}
                  Baseline: {acknowledgingAlert.alert_type === "rate_variance" ? 
                    `${acknowledgingAlert.baseline_value?.toFixed(2)}%` : 
                    `$${acknowledgingAlert.baseline_value?.toLocaleString()}`}
                </div>
                <div className="text-sm font-medium text-red-600 mt-1">
                  Variance: {formatVariance(acknowledgingAlert)}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Explain this variance (required for audit compliance):
                </label>
                <Textarea
                  placeholder="Provide explanation for this variance (e.g., seasonal labor costs, new contract requirements, facility changes, etc.)"
                  value={acknowledgmentComment}
                  onChange={(e) => setAcknowledgmentComment(e.target.value)}
                  rows={4}
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAcknowledgingAlert(null);
                  setAcknowledgmentComment("");
                  setError(null);
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleAcknowledgeAlert(acknowledgingAlert._id || acknowledgingAlert.id)}
                disabled={loading || !acknowledgmentComment.trim()}
              >
                {loading ? "Acknowledging..." : "Acknowledge Variance"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VarianceAlerts;